import {
    createTaskComment,
    deleteTaskComment,
    getTaskComments,
    updateTaskComment,
    type CreateTaskCommentInput,
    type TaskCommentListResponse,
    type TaskCommentQuery,
    type UpdateTaskCommentInput,
} from "@dashboard/api/task";

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

export const taskCommentKeys = {
    all: ["taskComments"] as const,
    lists: () => [...taskCommentKeys.all, "list"] as const,
    list: (taskId: number, query?: TaskCommentQuery) =>
        [...taskCommentKeys.lists(), taskId, query] as const,
    listsByTaskId: (taskId: number) => [...taskCommentKeys.lists(), taskId] as const,
};

export const useTaskComments = (
    taskId: number,
    query?: TaskCommentQuery,
    options?: Omit<UseQueryOptions<TaskCommentListResponse>, "queryKey" | "queryFn">,
) => {
    return useQuery({
        queryKey: taskCommentKeys.list(taskId, query),
        queryFn: () => getTaskComments(taskId, query),
        enabled: !!taskId && options?.enabled !== false,
        ...options,
    });
};

export const useCreateTaskComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ taskId, data }: { taskId: number; data: CreateTaskCommentInput }) =>
            createTaskComment(taskId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: taskCommentKeys.listsByTaskId(variables.taskId),
            });
        },
    });
};

export const useUpdateTaskComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            commentId,
            data,
        }: {
            taskId: number;
            commentId: number;
            data: UpdateTaskCommentInput;
        }) => updateTaskComment(commentId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: taskCommentKeys.listsByTaskId(variables.taskId),
            });
        },
    });
};

export const useDeleteTaskComment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ commentId }: { taskId: number; commentId: number }) =>
            deleteTaskComment(commentId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: taskCommentKeys.listsByTaskId(variables.taskId),
            });
        },
    });
};
